import type { BodyPart } from "@/lib/activityAnatomy";

/** Strip author HTML to something the browser can read out loud. */
export function htmlToSpeakable(html: string) {
  if (typeof DOMParser === "undefined") {
    return String(html || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  doc.querySelectorAll("script, style, noscript").forEach((node) => node.remove());
  doc.querySelectorAll("br").forEach((node) => node.replaceWith("\n"));
  doc.querySelectorAll("p, li, h1, h2, h3, h4, h5, h6, tr, div, blockquote").forEach((node) => {
    node.append("\n");
  });
  return String(doc.body.textContent || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/** Title, reading, image labels, and key terms. Not questions. */
export function activitySpeakText({
  title,
  parts,
  terms,
}: {
  title?: string | null;
  parts?: BodyPart[] | null;
  terms?: Array<{ term?: string; definition?: string }> | null;
}) {
  const blocks: string[] = [];
  const heading = String(title || "").trim();
  if (heading) blocks.push(heading);

  for (const part of parts || []) {
    if (part.type === "html") {
      const text = htmlToSpeakable(part.html);
      if (text) blocks.push(text);
      continue;
    }
    if (part.type === "image") {
      const alt = String(part.alt || "").trim();
      if (alt) blocks.push(alt);
    }
  }

  for (const row of terms || []) {
    const term = String(row.term || "").trim();
    const definition = String(row.definition || "").trim();
    if (term && definition) blocks.push(`${term}. ${definition}`);
    else if (term) blocks.push(term);
    else if (definition) blocks.push(definition);
  }

  return blocks.join("\n\n").trim();
}

/** Chrome drops long utterances. Speak in short sentences. */
export function speakChunks(text: string, max = 280) {
  const cleaned = String(text || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return [];
  const sentences = cleaned.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [cleaned];
  const chunks: string[] = [];
  let buf = "";
  for (const sentence of sentences) {
    const next = buf ? `${buf} ${sentence.trim()}` : sentence.trim();
    if (next.length > max && buf) {
      chunks.push(buf);
      buf = sentence.trim();
    } else {
      buf = next;
    }
  }
  if (buf) chunks.push(buf);
  return chunks;
}
